import { Nullable } from '@utils/util.types';
import {
	chatService,
	EditMessagePayload,
	GetMessagesParams,
	Message as ApiMessage,
	SendMessagePayload,
} from 'services/chat';
import { create } from 'zustand';
import { Message } from '../types/message';
import { MessageSendStatus, MessageStatus } from '../types/types';

// Enhanced store interface with API operations
export interface MessageStore {
	// Existing state
	channelMessages: {
		[key: string]: ApiMessage[];
	};

	channelDrafts: {
		[key: string]: Message;
	};

	editorDrafts: {
		[key: string]: string;
	};

	pendingMessages: {
		[channelId: string]: Message[];
	};

	// Client ID to Server ID mapping for pending messages
	clientToServerIdMap: {
		[clientId: string]: string;
	};
	
	// Search results state
	searchResults: {
		[channelId: string]: ApiMessage[];
	};
	
	// Search loading states
	searchLoadingStates: {
		[channelId: string]: {
			isSearching: boolean;
			isLoadingMoreResults: boolean;
			isReachingEnd: boolean;
			error: string | null;
		};
	};

	// Loading states
	loadingStates: {
		[channelId: string]: {
			isLoading: boolean;
			isLoadingMore: boolean;
			isReachingEnd: boolean;
			error: string | null;
		};
	};

	// Pagination state
	paginationState: {
		[channelId: string]: {
			currentPage: number;
			lastMessageId?: string;
			limit: number;
		};
	};

	// Message operations state
	operationStates: {
		sending: { [messageId: string]: boolean };
		editing: { [messageId: string]: boolean };
		deleting: { [messageId: string]: boolean };
	};

	// Existing getters
	getChannelMessages: (channelId: Nullable<string>) => ApiMessage[];
	getChannelDraft: (channelId: string) => Message | null;
	getEditorDraft: (channelId: string) => string;
	setEditorDraft: (channelId: string, content: string) => void;
	getPendingMessages: (channelId: string) => Message[];
	addPendingMessage: (channelId: string, message: Message) => void;
	updatePendingMessage: (
		channelId: string,
		messageId: string,
		status: MessageSendStatus,
		attachments?: any[]
	) => void;
	removePendingMessage: (channelId: string, messageId: string) => void;
	setChannelMessages: (channelId: Nullable<string>, messages: ApiMessage[]) => void;
	setChannelDraft: (channelId: string, draft: Message) => void;
	deleteMessage: (channelId: Nullable<string>, messageId: string) => void;

	// New API operations
	fetchMessages: (channelId: string, params?: GetMessagesParams) => Promise<ApiMessage[]>;
	loadMoreMessages: (channelId: string) => Promise<ApiMessage[]>;
	sendMessage: (channelId: string, payload: SendMessagePayload) => Promise<ApiMessage | null>;
	editMessage: (
		channelId: string,
		messageId: string,
		payload: EditMessagePayload
	) => Promise<ApiMessage | null>;
	deleteMessageFromAPI: (channelId: string, messageId: string) => Promise<boolean>;
	
	// Search operations
	searchMessages: (channelId: string, query: string) => Promise<ApiMessage[]>;
	loadMoreSearchResults: (channelId: string, query: string) => Promise<ApiMessage[]>;
	getSearchResults: (channelId: string) => ApiMessage[];
	clearSearchResults: (channelId: string) => void;
	setSearchLoadingState: (channelId: string, isSearching: boolean) => void;
	setSearchError: (channelId: string, error: string | null) => void;

	// State management helpers
	setLoadingState: (channelId: string, loading: boolean) => void;
	setLoadingMoreState: (channelId: string, loadingMore: boolean) => void;
	setError: (channelId: string, error: string | null) => void;
	clearChannelData: (channelId: string) => void;

	// WebSocket message handlers
	handleWebSocketMessage: (message: ApiMessage) => void;
	handleWebSocketMessageEdit: (
		messageId: string,
		channelId: string,
		updatedMessage: ApiMessage
	) => void;
	handleWebSocketMessageDelete: (messageId: string, channelId: string) => void;
}

export const useMessageStore = create<MessageStore>((set, get) => ({
	channelMessages: {},
	channelDrafts: {},
	editorDrafts: {},
	pendingMessages: {},
	clientToServerIdMap: {},
	loadingStates: {},
	paginationState: {},
	operationStates: {
		sending: {},
		editing: {},
		deleting: {},
	},
	
	// Search-related state
	searchResults: {},
	searchLoadingStates: {},

	// Existing getters
	getChannelMessages: (channelId: Nullable<string>) => {
		if (!channelId) return [];
		return get().channelMessages[channelId] || [];
	},

	getChannelDraft: (channelId: string) => {
		return get().channelDrafts[channelId] || null;
	},

	getEditorDraft: (channelId: string) => {
		return get().editorDrafts[channelId] || '';
	},

	setEditorDraft: (channelId: string, content: string) => {
		set((state) => ({
			editorDrafts: {
				...state.editorDrafts,
				[channelId]: content,
			},
		}));
	},

	getPendingMessages: (channelId: string) => {
		return get().pendingMessages[channelId] || [];
	},

	addPendingMessage: (channelId: string, message: Message) => {
		set((state) => {
			const channelPendingMessages = state.pendingMessages[channelId] || [];
			return {
				pendingMessages: {
					...state.pendingMessages,
					[channelId]: [...channelPendingMessages, message],
				},
			};
		});
	},

	updatePendingMessage: (
		channelId: string,
		messageId: string,
		status: MessageSendStatus,
		attachments?: any[]
	) => {
		set((state) => {
			const channelPendingMessages = state.pendingMessages[channelId] || [];
			const updatedMessages = channelPendingMessages.map((msg) => {
				if (msg.id === messageId) {
					const newMessage = new Message(
						msg.id,
						msg.author,
						msg.channelId,
						msg.contents,
						msg.createDate.getTime() / 1000,
						msg.authorDto,
						status,
						msg.messageStatus,
						msg.replyTo
					);

					newMessage.editDate = new Date();

					if (attachments && attachments.length > 0) {
						newMessage.setAttachments(attachments);
					} else if (msg.attachments && msg.attachments.length > 0) {
						newMessage.setAttachments(msg.attachments);
					}

					return newMessage;
				}
				return msg;
			});

			return {
				pendingMessages: {
					...state.pendingMessages,
					[channelId]: updatedMessages,
				},
			};
		});
	},

	removePendingMessage: (channelId: string, messageId: string) => {
		set((state) => {
			const channelPendingMessages = state.pendingMessages[channelId] || [];
			const filteredMessages = channelPendingMessages.filter((msg) => msg.id !== messageId);

			return {
				pendingMessages: {
					...state.pendingMessages,
					[channelId]: filteredMessages,
				},
			};
		});
	},

	deleteMessage: (channelId: Nullable<string>, messageId: string) => {
		if (!channelId) return;

		set((state) => {
			const channelMessages = state.channelMessages[channelId] || [];
			const updatedMessages = channelMessages.map((message) => {
				if (message.id === messageId) {
					return {
						...message,
						attachments: [],
						content: null,
						status: MessageStatus.Deleted,
					};
				}
				return message;
			});

			const pendingMessages = state.pendingMessages[channelId] || [];
			const updatedPendingMessages = pendingMessages.filter((msg) => msg.id !== messageId);

			return {
				channelMessages: {
					...state.channelMessages,
					[channelId]: updatedMessages,
				},
				pendingMessages: {
					...state.pendingMessages,
					[channelId]: updatedPendingMessages,
				},
			};
		});
	},

	setChannelMessages: (channelId: Nullable<string>, messages: ApiMessage[]) => {
		if (!channelId) return;

		set((state) => ({
			channelMessages: {
				...state.channelMessages,
				[channelId]: [...messages],
			},
		}));
	},

	setChannelDraft: (channelId: string, draft: Message) => {
		set((state) => ({
			channelDrafts: {
				...state.channelDrafts,
				[channelId]: draft,
			},
		}));
	},

	// New API operations
	fetchMessages: async (channelId: string, params: GetMessagesParams = {}) => {
		const { setLoadingState, setError } = get();

		try {
			setLoadingState(channelId, true);
			setError(channelId, null);

			const messages = await chatService.getMessages(channelId, params);

			set((state) => {
				// Initialize pagination state
				const currentPagination = state.paginationState[channelId] || {
					currentPage: 0,
					limit: params.limit || 20,
				};

				return {
					channelMessages: {
						...state.channelMessages,
						[channelId]: messages,
					},
					paginationState: {
						...state.paginationState,
						[channelId]: {
							...currentPagination,
							lastMessageId: messages.length > 0 ? messages[messages.length - 1].id : undefined,
						},
					},
					loadingStates: {
						...state.loadingStates,
						[channelId]: {
							...(state.loadingStates[channelId] || {}),
							isLoading: false,
							isReachingEnd: messages.length < (params.limit || 20),
							error: null,
						},
					},
				};
			});

			return messages;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to fetch messages';
			setError(channelId, errorMessage);
			setLoadingState(channelId, false);
			throw error;
		}
	},

	loadMoreMessages: async (channelId: string) => {
		const state = get();
		const pagination = state.paginationState[channelId];
		const loadingState = state.loadingStates[channelId];

		if (!pagination || loadingState?.isLoadingMore || loadingState?.isReachingEnd) {
			return [];
		}

		try {
			get().setLoadingMoreState(channelId, true);

			const messages = await chatService.getMessages(channelId, {
				lastMessageId: pagination.lastMessageId,
				limit: pagination.limit,
				order: 'desc',
			});

			set((currentState) => {
				const existingMessages = currentState.channelMessages[channelId] || [];

				return {
					channelMessages: {
						...currentState.channelMessages,
						[channelId]: [...existingMessages, ...messages],
					},
					paginationState: {
						...currentState.paginationState,
						[channelId]: {
							...pagination,
							currentPage: pagination.currentPage + 1,
							lastMessageId:
								messages.length > 0 ? messages[messages.length - 1].id : pagination.lastMessageId,
						},
					},
					loadingStates: {
						...currentState.loadingStates,
						[channelId]: {
							...(currentState.loadingStates[channelId] || {}),
							isLoadingMore: false,
							isReachingEnd: messages.length < pagination.limit,
						},
					},
				};
			});

			return messages;
		} catch (error) {
			get().setLoadingMoreState(channelId, false);
			get().setError(
				channelId,
				error instanceof Error ? error.message : 'Failed to load more messages'
			);
			throw error;
		}
	},

	sendMessage: async (channelId: string, payload: SendMessagePayload) => {
		try {
			console.log('🔄 Sending message via API:', payload.messageId);
			
			// Set sending state immediately
			set((state) => ({
				operationStates: {
					...state.operationStates,
					sending: {
						...state.operationStates.sending,
						[payload.messageId]: true,
					},
				},
			}));

			// Send to API
			const newMessage = await chatService.sendMessage(channelId, payload);
			
			console.log('✅ Message sent successfully via API:', {
				clientId: payload.messageId,
				serverId: newMessage.id,
				channelId: channelId
			});

			// Map client ID to server ID for later WebSocket matching
			set((state) => ({
				...state,
				clientToServerIdMap: {
					...state.clientToServerIdMap,
					[payload.messageId]: newMessage.id,
				},
				operationStates: {
					...state.operationStates,
					sending: {
						...state.operationStates.sending,
						[payload.messageId]: false,
					},
				},
			}));

			return newMessage;
		} catch (error) {
			console.error('❌ Message send failed via API:', {
				clientId: payload.messageId,
				channelId: channelId,
				error: error
			});
			
			// Reset sending state on error
			set((state) => ({
				operationStates: {
					...state.operationStates,
					sending: {
						...state.operationStates.sending,
						[payload.messageId]: false,
					},
				},
			}));
			
			throw error;
		}
	},

	editMessage: async (channelId: string, messageId: string, payload: EditMessagePayload) => {
		try {
			set((state) => ({
				operationStates: {
					...state.operationStates,
					editing: {
						...state.operationStates.editing,
						[messageId]: true,
					},
				},
			}));

			const updatedMessage = await chatService.editMessage(channelId, messageId, payload);

			// Update message in channel messages
			set((state) => {
				const channelMessages = state.channelMessages[channelId] || [];
				const updatedMessages = channelMessages.map((msg) =>
					msg.id === messageId ? updatedMessage : msg
				);

				return {
					channelMessages: {
						...state.channelMessages,
						[channelId]: updatedMessages,
					},
					operationStates: {
						...state.operationStates,
						editing: {
							...state.operationStates.editing,
							[messageId]: false,
						},
					},
				};
			});

			return updatedMessage;
		} catch (error) {
			set((state) => ({
				operationStates: {
					...state.operationStates,
					editing: {
						...state.operationStates.editing,
						[messageId]: false,
					},
				},
			}));
			throw error;
		}
	},

	deleteMessageFromAPI: async (channelId: string, messageId: string) => {
		try {
			set((state) => ({
				operationStates: {
					...state.operationStates,
					deleting: {
						...state.operationStates.deleting,
						[messageId]: true,
					},
				},
			}));

			await chatService.deleteMessage(channelId, messageId);

			// Update message status to deleted
			get().deleteMessage(channelId, messageId);

			set((state) => ({
				operationStates: {
					...state.operationStates,
					deleting: {
						...state.operationStates.deleting,
						[messageId]: false,
					},
				},
			}));

			return true;
		} catch (error) {
			set((state) => ({
				operationStates: {
					...state.operationStates,
					deleting: {
						...state.operationStates.deleting,
						[messageId]: false,
					},
				},
			}));
			return false;
		}
	},

	// Search operations
	searchMessages: async (channelId: string, query: string) => {
		try {
			get().setSearchLoadingState(channelId, true);
			get().setSearchError(channelId, null);

			const results = await chatService.searchMessages(channelId, query, {
				limit: 20,
				order: 'desc',
			});

			set((state) => ({
				searchResults: {
					...state.searchResults,
					[channelId]: results,
				},
				searchLoadingStates: {
					...state.searchLoadingStates,
					[channelId]: {
						...(state.searchLoadingStates[channelId] || {}),
						isReachingEnd: results.length < 20,
					},
				},
			}));

			get().setSearchLoadingState(channelId, false);
			return results;
		} catch (error) {
			get().setSearchLoadingState(channelId, false);
			get().setSearchError(channelId, error instanceof Error ? error.message : 'Search failed');
			return [];
		}
	},

	loadMoreSearchResults: async (channelId: string, query: string) => {
		const state = get();
		const currentResults = state.searchResults[channelId] || [];
		const searchLoadingState = state.searchLoadingStates[channelId];

		if (searchLoadingState?.isLoadingMoreResults || searchLoadingState?.isReachingEnd) {
			return [];
		}

		try {
			set((state) => ({
				searchLoadingStates: {
					...state.searchLoadingStates,
					[channelId]: {
						...(state.searchLoadingStates[channelId] || {}),
						isLoadingMoreResults: true,
					},
				},
			}));

			const lastMessageId = currentResults.length > 0 ? currentResults[currentResults.length - 1].id : '';
			const newResults = await chatService.searchMessages(channelId, query, {
				lastMessageId,
				limit: 20,
				order: 'desc',
			});

			const updatedResults = [...currentResults, ...newResults];

			set((state) => ({
				searchResults: {
					...state.searchResults,
					[channelId]: updatedResults,
				},
				searchLoadingStates: {
					...state.searchLoadingStates,
					[channelId]: {
						...(state.searchLoadingStates[channelId] || {}),
						isLoadingMoreResults: false,
						isReachingEnd: newResults.length < 20,
					},
				},
			}));

			return newResults;
		} catch (error) {
			set((state) => ({
				searchLoadingStates: {
					...state.searchLoadingStates,
					[channelId]: {
						...(state.searchLoadingStates[channelId] || {}),
						isLoadingMoreResults: false,
					},
				},
			}));
			get().setSearchError(channelId, error instanceof Error ? error.message : 'Load more search failed');
			return [];
		}
	},

	getSearchResults: (channelId: string) => {
		return get().searchResults[channelId] || [];
	},

	clearSearchResults: (channelId: string) => {
		set((state) => ({
			searchResults: {
				...state.searchResults,
				[channelId]: [],
			},
			searchLoadingStates: {
				...state.searchLoadingStates,
				[channelId]: {
					isSearching: false,
					isLoadingMoreResults: false,
					isReachingEnd: false,
					error: null,
				},
			},
		}));
	},

	setSearchLoadingState: (channelId: string, isSearching: boolean) => {
		set((state) => ({
			searchLoadingStates: {
				...state.searchLoadingStates,
				[channelId]: {
					...(state.searchLoadingStates[channelId] || {}),
					isSearching,
				},
			},
		}));
	},

	setSearchError: (channelId: string, error: string | null) => {
		set((state) => ({
			searchLoadingStates: {
				...state.searchLoadingStates,
				[channelId]: {
					...(state.searchLoadingStates[channelId] || {}),
					error,
				},
			},
		}));
	},

	// State management helpers
	setLoadingState: (channelId: string, loading: boolean) => {
		set((state) => ({
			loadingStates: {
				...state.loadingStates,
				[channelId]: {
					...(state.loadingStates[channelId] || {}),
					isLoading: loading,
				},
			},
		}));
	},

	setLoadingMoreState: (channelId: string, loadingMore: boolean) => {
		set((state) => ({
			loadingStates: {
				...state.loadingStates,
				[channelId]: {
					...(state.loadingStates[channelId] || {}),
					isLoadingMore: loadingMore,
				},
			},
		}));
	},

	setError: (channelId: string, error: string | null) => {
		set((state) => ({
			loadingStates: {
				...state.loadingStates,
				[channelId]: {
					...(state.loadingStates[channelId] || {}),
					error,
				},
			},
		}));
	},

	clearChannelData: (channelId: string) => {
		set((state) => {
			const newChannelMessages = { ...state.channelMessages };
			const newPendingMessages = { ...state.pendingMessages };
			const newLoadingStates = { ...state.loadingStates };
			const newPaginationState = { ...state.paginationState };

			delete newChannelMessages[channelId];
			delete newPendingMessages[channelId];
			delete newLoadingStates[channelId];
			delete newPaginationState[channelId];

			return {
				channelMessages: newChannelMessages,
				pendingMessages: newPendingMessages,
				loadingStates: newLoadingStates,
				paginationState: newPaginationState,
			};
		});
	},

	// WebSocket message handlers
	handleWebSocketMessage: (message: ApiMessage) => {
		console.log('📨 handleWebSocketMessage called:', {
			messageId: message.id,
			channelId: message.channelId,
			source: 'store'
		});
		
		set((state) => {
			const channelId = message.channelId;
			const existingMessages = state.channelMessages[channelId] || [];
			const pendingMessages = state.pendingMessages[channelId] || [];

			// Quick duplicate check - use Map for O(1) lookup instead of Array.some which is O(n)
			const existingIds = new Set(existingMessages.map((msg) => msg.id));
			if (existingIds.has(message.id)) {
				console.log('🟡 Message already exists in store, skipping:', message.id);
				return state; // No state change needed
			}

			// Find and remove pending message using both direct ID match and client-to-server mapping
			// Use state instead of get() to avoid circular dependency
			const currentClientToServerIdMap = state.clientToServerIdMap;
			const pendingMessagesBefore = pendingMessages.length;

			// First, try direct ID match
			let updatedPendingMessages = pendingMessages.filter((msg) => msg.id !== message.id);
			let updatedClientToServerIdMap = currentClientToServerIdMap;

			// If no direct match, try using client-to-server ID mapping
			if (updatedPendingMessages.length === pendingMessagesBefore) {
				// Look for a client ID that maps to this server ID
				const clientId = Object.keys(currentClientToServerIdMap).find(
					(key) => currentClientToServerIdMap[key] === message.id
				);

				if (clientId) {
					updatedPendingMessages = pendingMessages.filter((msg) => msg.id !== clientId);

					// Clean up the mapping after use
					updatedClientToServerIdMap = { ...currentClientToServerIdMap };
					delete updatedClientToServerIdMap[clientId];
				}
			}

			// Add to confirmed messages at the beginning (newest first)
			const updatedConfirmedMessages = [message, ...existingMessages];

			console.log('✅ Message successfully added to store:', {
				messageId: message.id,
				channelId: channelId,
				totalMessages: updatedConfirmedMessages.length,
				removedPending: pendingMessagesBefore - updatedPendingMessages.length
			});

			// Return single state update
			return {
				...state,
				channelMessages: {
					...state.channelMessages,
					[channelId]: updatedConfirmedMessages,
				},
				pendingMessages: {
					...state.pendingMessages,
					[channelId]: updatedPendingMessages,
				},
				clientToServerIdMap: updatedClientToServerIdMap,
			};
		});
	},

	handleWebSocketMessageEdit: (
		messageId: string,
		channelId: string,
		updatedMessage: ApiMessage
	) => {
		set((state) => {
			const channelMessages = state.channelMessages[channelId] || [];
			const messageFound = channelMessages.find((msg) => msg.id === messageId);

			if (!messageFound) {
				return state;
			}

			const updatedMessages = channelMessages.map((msg) =>
				msg.id === messageId ? updatedMessage : msg
			);

			return {
				channelMessages: {
					...state.channelMessages,
					[channelId]: updatedMessages,
				},
			};
		});
	},

	handleWebSocketMessageDelete: (messageId: string, channelId: string) => {
		get().deleteMessage(channelId, messageId);
	},
}));
